import { useEffect, useState } from "react";
import { CalendarOff, ChevronLeft, ChevronRight, Info } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { Popover } from "antd";
import { getAppointment } from "../../redux/action/Appointment";

export default function UnavailabilityCalendar({
  setStart,
  setEnd,
  setReason,
  setIsAvaiable,
}) {
  const today = new Date();

  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectingEnd, setSelectingEnd] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const dispatch = useDispatch();
  const { data, loading } = useSelector((state) => state.GET_APPOINTMENT);

  const getAppointmentData = () => {
    const payload = {
      upcomingAppointment: true,
    };
    dispatch(getAppointment(payload, 500));
  };

  useEffect(() => {
    getAppointmentData();
    // const interval = setInterval(getAppointmentData, 5000);
    // return () => clearInterval(interval);
  }, []);

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(currentYear, currentMonth, day);

    if (!selectingEnd) {
      // First click: set start date
      setStartDate(clickedDate);
      setStart(clickedDate);
      setEndDate(null);
      setEnd(null);
      setIsAvaiable(false); // Reset to true initially
      setSelectingEnd(true);
    } else {
      let start = startDate;
      let end = clickedDate;

      if (clickedDate < startDate) {
        // Swap if clicked date is before startDate
        start = clickedDate;
        end = startDate;
      }

      // Set final range
      setStartDate(start);
      setStart(start);
      setEndDate(end);
      setEnd(end);
      setIsAvaiable(false);
      // Check appointments in the selected range
      checkAvailabilityBetweenDates(start, end);
      if (checkAvailabilityBetweenDates(start, end)) {
        setIsAvaiable(true);
      } else {
        setIsAvaiable(false);
      }
      console.log("here====>", checkAvailabilityBetweenDates(start, end));

      // Reset selecting
      setSelectingEnd(false);
    }
  };

  const resetSelection = () => {
    setStartDate(null);
    setEndDate(null);
    setStart(null);
    setEnd(null);
    setSelectingEnd(false);
  };

  const hasAppointment = (year, month, day) => {
    const dateString = `${year}-${String(month + 1).padStart(2, "0")}-${String(
      day
    ).padStart(2, "0")}`;
    console.log(dateString);

    return (
      Object.prototype.hasOwnProperty.call(data, dateString) &&
      Array.isArray(data[dateString]) &&
      data[dateString].length > 0
    );
  };

  const checkAvailabilityBetweenDates = (start, end) => {
    let current = new Date(start);

    while (current <= end) {
      const dateString = `${current.getFullYear()}-${String(
        current.getMonth() + 1
      ).padStart(2, "0")}-${String(current.getDate()).padStart(2, "0")}`;

      if (
        Object.prototype.hasOwnProperty.call(data, dateString) &&
        Array.isArray(data[dateString]) &&
        data[dateString].length > 0
      ) {
        setIsAvaiable(false);
        return;
      }

      // Go to next day
      current.setDate(current.getDate() + 1);
    }

    // No appointments found in the range
    setIsAvaiable(true);
  };

  const isToday = (year, month, day) => {
    return (
      day === today.getDate() &&
      month === today.getMonth() &&
      year === today.getFullYear()
    );
  };

  const isInRange = (year, month, day) => {
    if (!startDate || !endDate) return false;
    const date = new Date(year, month, day);
    return date >= startDate && date <= endDate;
  };

  const isRangeStart = (year, month, day) => {
    if (!startDate) return false;
    const date = new Date(year, month, day);
    return date.getTime() === startDate.getTime();
  };

  const isRangeEnd = (year, month, day) => {
    if (!endDate) return false;
    const date = new Date(year, month, day);
    return date.getTime() === endDate.getTime();
  };

  const formatDate = (date) => {
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${monthNames[date.getMonth()]} ${date.getDate()}`;
  };

  const renderCalendar = () => {
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
    const daysOfWeek = ["S", "M", "T", "W", "T", "F", "S"];
    const blanks = Array(firstDayOfMonth).fill(null);
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    const calendarDays = [...blanks, ...days];
    const weeks = [];
    let week = [];

    calendarDays.forEach((day, i) => {
      week.push(day);
      if ((i + 1) % 7 === 0 || i === calendarDays.length - 1) {
        weeks.push(week);
        week = [];
      }
    });

    return (
      <div className="mt-0">
        <div className="grid grid-cols-7 text-center mb-1.5 bg-[#EFF6FF] rounded-lg">
          {daysOfWeek.map((day, index) => (
            <div
              key={index}
              className={`font-semibold font-poppins text-[11px] py-1.5 ${
                index === 0 || index === 6 ? "text-[#E11D48]" : "text-[#2563EB]"
              }`}
            >
              {day}
            </div>
          ))}
        </div>

        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7 text-center">
            {week.map((day, dayIndex) => {
              if (day === null) {
                return (
                  <div key={dayIndex} className="h-7 md:h-8 p-0.5 relative" />
                );
              }

              const inRange = isInRange(currentYear, currentMonth, day);
              const isRangeStartDate = isRangeStart(
                currentYear,
                currentMonth,
                day
              );
              const isRangeEndDate = isRangeEnd(currentYear, currentMonth, day);
              const hasAppointmentDot = hasAppointment(
                currentYear,
                currentMonth,
                day
              );
              const isTodayDate = isToday(currentYear, currentMonth, day);
              const isPastDate =
                new Date(currentYear, currentMonth, day) <
                new Date(today.getFullYear(), today.getMonth(), today.getDate());
              const isEdgeDate = isRangeStartDate || isRangeEndDate;

              let tileColor = "bg-[#EFF6FF] text-[#1E3A8A] hover:bg-[#DBEAFE]";
              if (isPastDate) tileColor = "bg-transparent text-[#CBD5E1]";
              if (hasAppointmentDot && !isPastDate)
                tileColor = "bg-[#D1FAE5] text-[#047857] font-semibold";
              if (inRange && !isEdgeDate)
                tileColor = "bg-transparent text-[#B91C1C] font-semibold";
              if (isTodayDate)
                tileColor = "bg-[#0F172A] text-white font-semibold shadow-md";
              if (isEdgeDate)
                tileColor = "bg-[#DC2626] text-white font-semibold shadow-md";

              let rangeBorderRadius = "";
              if (isRangeStartDate) rangeBorderRadius += "rounded-l-[50px] ";
              if (isRangeEndDate) rangeBorderRadius += "rounded-r-[50px] ";

              return (
                <div
                  key={dayIndex}
                  className={`h-7 md:h-8 flex justify-center items-center relative cursor-pointer ${
                    inRange ? `bg-[#FEE2E2] ${rangeBorderRadius}` : ""
                  }`}
                  onClick={() => handleDateClick(day)}
                >
                  <div
                    className={`flex items-center justify-center h-7 w-7 mx-auto rounded-lg text-xs font-poppins transition-colors ${tileColor}`}
                  >
                    {day}
                    {hasAppointmentDot && (
                      <div className="absolute bottom-0.5 left-1/2 transform -translate-x-1/2">
                        <div className="h-1 w-1 bg-[#10B981] rounded-full"></div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  const getMonthName = (month) => {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return monthNames[month];
  };

  return (
    <div className="max-w bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl shadow-sm p-[16px] mt-1">
      <div className="flex items-center gap-2.5 pb-2.5 mb-3 border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9]">
        <div className="flex items-center justify-center w-[32px] h-[32px] rounded-lg bg-[#FFF1F2] text-[#E11D48] flex-shrink-0">
          <CalendarOff size={16} />
        </div>
        <div className="flex items-center">
          <h1 className="text-base font-semibold text-[#171717] font-poppins mb-0">
            Set Your Unavailability
          </h1>
          <Popover
            trigger="click"
            placement="bottomLeft"
            content={
              <p className="text-[#334155] text-xs font-poppins mb-0 max-w-[260px]">
                Mark Time You're Unavailable. Let others know when you're not accepting
                appointments
              </p>
            }
          >
            <span
              role="button"
              aria-label="More information"
              className="ml-2 flex items-center justify-center w-6 h-6 rounded-full cursor-pointer hover:bg-[#F1F5F9] transition-colors"
            >
              <Info className="text-[#94A3B8] h-3.5 w-3.5" />
            </span>
          </Popover>
        </div>
      </div>

      <div className="w-full max-w-[340px] mx-auto">
        <div className="flex items-center justify-between mb-2">
          <button
            type="button"
            onClick={prevMonth}
            className="flex items-center justify-center w-8 h-8 p-0 rounded-lg bg-white shadow-sm hover:bg-[#F8FAFC] transition-colors"
            aria-label="Previous month"
            style={{ border: "1px solid #E2E8F0" }}
          >
            <ChevronLeft size={18} className="text-[#334155]" />
          </button>

          <div className="flex items-center">
            <span className="text-[#171717] font-poppins font-semibold text-[14px]">
              {getMonthName(currentMonth)} {currentYear}
            </span>
          </div>

          <button
            type="button"
            onClick={nextMonth}
            className="flex items-center justify-center w-8 h-8 p-0 rounded-lg bg-white shadow-sm hover:bg-[#F8FAFC] transition-colors"
            aria-label="Next month"
            style={{ border: "1px solid #E2E8F0" }}
          >
            <ChevronRight size={18} className="text-[#334155]" />
          </button>
        </div>

        <div className="flex justify-between items-center min-h-[28px] text-sm mb-2">
          <div>
            {startDate && !endDate && (
              <span className="font-poppins text-xs text-[#64748B]">Select end date</span>
            )}
            {(startDate || endDate) && (
              <span className="font-poppins text-xs font-semibold text-[#DC2626] bg-[#FEF2F2] px-2.5 py-1 rounded-full ml-1">
                Selected: {startDate && formatDate(startDate)} - {endDate && formatDate(endDate)}
              </span>
            )}
          </div>
          {(startDate || endDate) && (
            <button
              type="button"
              onClick={resetSelection}
              className="text-[#DC2626] hover:text-red-700 text-xs font-semibold px-0 py-0 transition-colors bg-transparent border-0 font-poppins"
            >
              Clear
            </button>
          )}
        </div>

        <div className="w-full">{renderCalendar()}</div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-[11px] font-poppins text-[#64748B]">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-[4px] bg-[#0F172A]"></span>Today
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-[4px] bg-[#D1FAE5] border-[1px] border-solid border-[#10B981]"></span>Appointments
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-[4px] bg-[#DC2626]"></span>Selected
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-[4px] bg-[#EFF6FF] border-[1px] border-solid border-[#BFDBFE]"></span>Available
          </span>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t-[1px] border-solid border-x-0 border-b-0 border-[#F1F5F9]">
        <h2 className="text-sm font-semibold text-[#171717] font-poppins mb-2">
          Unavailability Reason{" "}
          <span className="text-[#64748B] text-xs font-normal font-poppins">
            (optional)
          </span>
        </h2>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={`px-3.5 py-1.5 text-xs font-semibold rounded-full font-poppins border-0 transition-all ${
              selectedReason === "Personal Leave"
                ? `bg-[#0F172A] text-white shadow-md`
                : `bg-[#F1F5F9] text-[#334155] hover:bg-[#E2E8F0]`
            }`}
            onClick={() => {
              setSelectedReason("Personal Leave");
              setReason("Personal Leave");
            }}
          >
            Personal Leave
          </button>
          <button
            type="button"
            className={`px-3.5 py-1.5 text-xs font-semibold rounded-full font-poppins border-0 transition-all ${
              selectedReason === "Clinic Closed"
                ? `bg-[#0F172A] text-white shadow-md`
                : `bg-[#F1F5F9] text-[#334155] hover:bg-[#E2E8F0]`
            }`}
            onClick={() => {
              setSelectedReason("Clinic Closed");
              setReason("Clinic Closed");
            }}
          >
            Clinic Closed
          </button>
          <button
            type="button"
            className={`px-3.5 py-1.5 text-xs font-semibold rounded-full font-poppins border-0 transition-all ${
              selectedReason === "Custom Reason"
                ? `bg-[#0F172A] text-white shadow-md`
                : `bg-[#F1F5F9] text-[#334155] hover:bg-[#E2E8F0]`
            }`}
            onClick={() => setSelectedReason("Custom Reason")}
          >
            Custom Reason
          </button>
        </div>
        {selectedReason === "Custom Reason" && (
          <textarea
            className="w-full max-w-[340px] h-[80px] bg-[#F8FAFC] mt-3 p-3 font-poppins text-sm text-[#334155] focus:outline-none focus:ring-2 focus:ring-[#B3D7FF]"
            style={{
              borderRadius: "12px",
              border: "1px solid #E2E8F0",
            }}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Type here..."
          ></textarea>
        )}
      </div>
    </div>
  );
}
